import { BrowserModule } from "@angular/platform-browser";
import { NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { RouterModule } from "@angular/router";

import { AppComponent } from "./app.component";
import { NavMenuComponent } from "./nav-menu/nav-menu.component";
import { HomeComponent } from "./home/home.component";
import { CounterComponent } from "./counter/counter.component";
import { FetchDataComponent } from "./fetch-data/fetch-data.component";
import { ClinicalSurveyComponent } from "./clinical-survey/clinical-survey.component";
import { NonClinicalSurveyComponent } from "./non-clinical-survey/non-clinical-survey.component";
import { VerificationComponent } from "./verification/verification.component";

//Angular Material Components
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatNativeDateModule } from "@angular/material/core";
import { MatButtonModule } from "@angular/material/button";
import { MatInputModule } from "@angular/material/input";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import { MatSliderModule } from "@angular/material/slider";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatMenuModule } from "@angular/material/menu";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatListModule } from "@angular/material/list";
import { MatGridListModule } from "@angular/material/grid-list";
import { MatCardModule } from "@angular/material/card";
import { MatStepperModule } from "@angular/material/stepper";
import { MatTabsModule } from "@angular/material/tabs";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatChipsModule } from "@angular/material/chips";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatDialogModule } from "@angular/material/dialog";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatTableModule } from "@angular/material/table";
import { MatSortModule } from "@angular/material/sort";
import { MatPaginatorModule } from "@angular/material/paginator";
import { SuccessDialogComponent } from "./dialogs/success-dialog/success-dialog.component";
import { SurveyComponent } from "./survey/survey.component";
import { EmpGuardService } from "./services/emp-guard.service";
import { SkipDialogComponent } from "./dialogs/skip-dialog/skip-dialog.component";
import { VolunteerDialogComponent } from "./dialogs/volunteer-dialog/volunteer-dialog.component";
import { VolunteerCalendarLegendDialogComponent } from "./dialogs/volunteer-dialog/volunteer-calendar-legend.component";
import { ErrorDialogComponent } from "./dialogs/error-dialog/error-dialog.component";
import { ViewPdfDialogComponent } from './dialogs/view-pdf-dialog/view-pdf-dialog.component';


import { ToastrModule } from "ngx-toastr";
import { PersonalInfoComponent } from "./personal-info/personal-info.component";
import { ThankYouComponent } from "./thank-you/thank-you.component";
import { VaxThankYouComponent } from "./thank-you/vax-thank-you.component";
import { NonVaxThankYouComponent } from "./thank-you/non-vax-thank-you.component ";
import { PreShiftGuardService } from "./services/pre-shift-guard.service";
import { ScreeningReportComponent } from "./screening-report/screening-report.component";
import { UsageReportComponent } from "./usage-report/usage-report.component";
import { AutoverifyComponent } from "./autoverify/autoverify.component";
import { GovSurveyGuardService } from "./services/gov-survey-guard.service";
import { PendingChangesGuardService } from "./services/pending-changes-guard.service";
import { LogoutComponent } from "./verification/logout.component";
import { DigitOnlyDirective } from "./directives/digit-only.directive";
import { VolunteerOpportunityComponent } from "./volunteer-opportunity/volunteer-opportunity.component";
import { VolunteerEventComponent } from "./volunteer-opportunity/volunteer-event.component";
import { DatepickerExpirationSelection } from "./covid-test/expiration-date.component";

//./verification/customheader
import { CustomHeader } from "./verification/customheader.component";
import { SafeHtmlPipe } from "./pipes/safe-html.pipe";
import { IpGuardService } from "./services/ip-guard.service";
import { UnauthorizedComponent } from "./unauthorized/unauthorized.component";
import { VolunteerGuardService } from "./services/volunteer-guard.service";
import { ShiftDialogComponent } from "./dialogs/shift-dialog/shift-dialog.component";
import { VolunteerCalendarComponent } from './volunteer-calendar/volunteer-calendar.component';
import { CameraComponent } from "./camera/camera.component";
import { WebcamModule } from "ngx-webcam";
import { CovidTestComponent } from './covid-test/covid-test.component';
import { CovidTestContainerComponennt } from "./covid-test/covid-test-container.component";
import { AppUpdateDialogComponent } from "./dialogs/app-update-dialog.component";
import { CovidExposureComponent } from './covid-exposure/covid-exposure.component';
import { ExposureReportComponent } from "./covid-exposure/exposure-report.component";
import { ExternalTestUploadComponent } from "./external-test-upload/external-test-upload.component";
import { LocationOrEventRedirectComponent } from "./location-redirect/location-or-event-redirect.component";
import { MonkeypoxReportComponent } from "./monkeypox-survey/monkeypox-report.component";
import { CheckBoxInputComponent } from './ui-elements/checkbox-input.component';
import { DateInputComponent } from './ui-elements/date-input.component';
import { DropdownComponent } from './ui-elements/dropdown.component';
import { RadioButtonInputComponent } from './ui-elements/radio-button-input.component';
import { RadioinputComponent } from './ui-elements/radio-input.component';
import { TextBoxComponent } from './ui-elements/text-box.component';
import { TextAreaComponent } from './ui-elements/text-area.component';
import { PeoplePickerComponent } from './ui-elements/people-picker.component';
import { UploadComponent } from './ui-elements/ui-file-upload.component';
import { ExemptionRequestSubmissionComponent } from './exemption-request-submission/exemption-request-submission.component';
import { MsformsurveyComponent } from './msformsurvey/msformsurvey.component';
import { ExemptionsecondaryReviewRequestComponent } from './exemption-secondary-review-request/exemption-secondary-review-request.component';
import { CovidReturnToWorkComponent } from './covid-return-to-work/covid-return-to-work.component';
import { CovidRtwNegativeTestComponent } from './covid-rtw-negative-test/covid-rtw-negative-test.component';
import { ReturnToWorkComponent } from './return-to-work/return-to-work.component';
import { ReturnToWorkAcknowledgementComponent } from './return-to-work-acknowledgement/return-to-work-acknowledgement.component';
import { CountdownDialogComponent } from "./dialogs/countdown-dialog/countdown-dialog.component";
import { SparkMomentSurveyComponent } from './spark-moment-survey/spark-moment-survey.component';
import { MssparkyformComponent } from './mssparkyform/mssparkyform.component';
import { JobDescSPARKYComponent } from "./ui-elements/jobDescSparkyForm.component";


@NgModule({
    declarations: [
        AppComponent,
        NavMenuComponent,
        HomeComponent,
        CounterComponent,
        FetchDataComponent,
        ClinicalSurveyComponent,
        NonClinicalSurveyComponent,
        VerificationComponent,
        SuccessDialogComponent,
        SurveyComponent,
        SkipDialogComponent,
        PersonalInfoComponent,
        ThankYouComponent,
        VaxThankYouComponent,
        NonVaxThankYouComponent,
        ScreeningReportComponent,
        UsageReportComponent,
        AutoverifyComponent,
        LogoutComponent,
        DigitOnlyDirective,
        CustomHeader,
        SafeHtmlPipe,
        UnauthorizedComponent,
        VolunteerOpportunityComponent,
        ViewPdfDialogComponent,
        VolunteerEventComponent,
        VolunteerDialogComponent,
        VolunteerCalendarLegendDialogComponent,
        ShiftDialogComponent,
        VolunteerCalendarComponent,
        ErrorDialogComponent,
        CameraComponent,
        CovidTestComponent,
        ExternalTestUploadComponent,
        CovidTestContainerComponennt,
        AppUpdateDialogComponent,
        DatepickerExpirationSelection,
        CovidExposureComponent,
        ExposureReportComponent,
        MonkeypoxReportComponent,
        LocationOrEventRedirectComponent,
        CheckBoxInputComponent,
        DateInputComponent,
        DropdownComponent,
        RadioButtonInputComponent,
        RadioinputComponent,
        TextBoxComponent,
        TextAreaComponent,
        UploadComponent,
        ExemptionRequestSubmissionComponent,
        MsformsurveyComponent,
        ExemptionsecondaryReviewRequestComponent,
        CovidReturnToWorkComponent,
        CovidRtwNegativeTestComponent,
        ReturnToWorkComponent,
        ReturnToWorkAcknowledgementComponent,
        CountdownDialogComponent,
        SparkMomentSurveyComponent,
        MssparkyformComponent,
        PeoplePickerComponent,
        JobDescSPARKYComponent
    ],
    imports: [
        BrowserModule.withServerTransition({ appId: "ng-cli-universal" }),
        HttpClientModule,
        WebcamModule,
        FormsModule,
        ReactiveFormsModule,
        RouterModule.forRoot([
            { path: "", pathMatch: "full", redirectTo: "home" },
            {
                path: "home",
                component: HomeComponent,
                canActivate: [EmpGuardService],
            },
            { path: "counter", component: CounterComponent },
            {
                path: "volunteer",
                component: VolunteerEventComponent,
                canActivate: [VolunteerGuardService],
            },
            {
                path: "volunteer-activities/:eventid",
                component: VolunteerOpportunityComponent,
                canActivate: [VolunteerGuardService],
            },
            {
                path: "volunteer-calendar",
                component: VolunteerCalendarComponent,
                canActivate: [VolunteerGuardService],
            },
            // {
            //   path: "survey/exemption-request-submission",
            //   component: SurveyComponent,
            // },
            {
                path: "exemption-secondary-review-request",
                component: ExemptionsecondaryReviewRequestComponent,
            },
            {
                path: "exemption-request-submission",
                component: ExemptionRequestSubmissionComponent,
            },
            {
                path: "return-to-work",
                component: ReturnToWorkComponent,
            },
            {
                path: "spark-moment-survey",
                component: SparkMomentSurveyComponent,
            },
            {
                path: "return-to-work-acknowledgement",
                component: ReturnToWorkAcknowledgementComponent,
            },
            {
                path: "covid-return-to-work",
                component: CovidReturnToWorkComponent,
            },
            {
                path: "covid-rtw-negative-test",
                component: CovidRtwNegativeTestComponent,
            },
            { path: "usage", component: UsageReportComponent },
            { path: "clinical-survey", component: ClinicalSurveyComponent },
            { path: "non-clinical-survey", component: NonClinicalSurveyComponent },
            { path: "logout", component: LogoutComponent },
            { path: "verify", pathMatch: "full", redirectTo: "verify/survey" },
            { path: "verify/:flag", component: VerificationComponent },
            { path: "location/:locationId", component: LocationOrEventRedirectComponent },
            { path: "event/:eventId", component: LocationOrEventRedirectComponent },
            {
                path: "autoverify/:appid/:corpid/:empid/:dateofbirth",
                component: AutoverifyComponent,
            },
            {
                path: "volunteerautoverify/:appid/:corpid/:empid/:dateofbirth",
                component: AutoverifyComponent,
            },
            {
                path: "exposureautoverify/:appid/:corpid/:empid/:dateofbirth",
                component: AutoverifyComponent,
            },
            { path: "personal-info", component: PersonalInfoComponent },
            { path: "thank-you", component: ThankYouComponent },
            { path: "vax-thank-you", component: VaxThankYouComponent },
            { path: "non-vax-thank-you", component: NonVaxThankYouComponent },
            {
                path: "covid-exposure",
                component: CovidExposureComponent,
                canActivate: [EmpGuardService]
            },
            { path: "screening-report", component: ScreeningReportComponent },
            { path: "exposure-report", component: ExposureReportComponent },
            { path: "monkeypox-report", component: MonkeypoxReportComponent },
            {
                path: "screening/:url",
                component: SurveyComponent,
                canActivate: [PreShiftGuardService],
            },
            {
                path: "survey/:url",
                component: SurveyComponent,
                canActivate: [EmpGuardService],
                canDeactivate: [PendingChangesGuardService],
            },
            {
                path: "gov-survey/:url",
                component: SurveyComponent,
                canActivate: [IpGuardService, GovSurveyGuardService],
                canDeactivate: [PendingChangesGuardService],
            },
            // {
            //   path: "volunteer",
            //   component: CounterComponent,
            //   canActivate: [VolunteerGuardService],
            // },
            { path: "unauthorized", component: UnauthorizedComponent },
            { path: "test", component: CovidTestContainerComponennt },
            { path: "test-upload", component: ExternalTestUploadComponent },
            { path: "**", redirectTo: "home" },
        ], {}),
        BrowserAnimationsModule,
        ToastrModule.forRoot(),
        HttpClientModule,
        MatCheckboxModule,
        MatButtonModule,
        MatInputModule,
        MatAutocompleteModule,
        MatNativeDateModule,
        MatDatepickerModule,
        MatFormFieldModule,
        MatRadioModule,
        MatSelectModule,
        MatSliderModule,
        MatSlideToggleModule,
        MatMenuModule,
        MatSidenavModule,
        MatToolbarModule,
        MatListModule,
        MatGridListModule,
        MatCardModule,
        MatStepperModule,
        MatTabsModule,
        MatExpansionModule,
        MatButtonToggleModule,
        MatChipsModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatProgressBarModule,
        MatDialogModule,
        MatTooltipModule,
        MatSnackBarModule,
        MatTableModule,
        MatSortModule,
        MatPaginatorModule
    ],
    providers: [],
    bootstrap: [AppComponent]
})
export class AppModule {}
